import React, { useContext, useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate, useBlocker } from 'react-router';
import Breadcrumbs from 'Components/DesignSystem/Breadcrumbs/Breadcrumbs';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Button from 'Components/DesignSystem/Button/Button';
import IterationComponent from './IterationComponent';
import { IterationContext, IterationContextProvider, IterationContextType } from '../../contexts/IterationContext';
import { CANCEL, CREATE, EDIT } from '../../constants/CSConstants';
import CategorySelector from './CategorySelector';
import { createIterationMethod, updateIterationMethod } from '../../services/IterationServices';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import ConfirmModal from 'Pages/GMS/ISR/Components/RuleComponents/CommitConfirmModal';
import { AppContext } from 'Context/AppContext';

type IterationConfigurationProps = {
    match: {
        params: {
            categoryid?: string;
            uid?: string;
        };
    };
};

const IterationConfiguration: React.FC<IterationConfigurationProps> = (props) => {
    const { params } = props.match;
    const { categoryid: categoryId, uid } = params;
    const navigate = useNavigate();
    const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
    const allowNavigationRef = useRef<boolean>(false);
    const appContext = useContext(AppContext);

    const {
        industryId,
        channelKey,
        fetchInitialState,
        resetState,
        iterationPayload,
        setIterationPayload,
        isEditMode,
        loading,
        hasUnsavedChanges,
        showCancelDialog,
        setShowCancelDialog,
    } = useContext<IterationContextType>(IterationContext);

    // Browser navigation protection
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    useEffect(() => {
        // Initialize state based on whether we're creating or editing
        fetchInitialState(uid, categoryId);
        appContext?.setEditInProgress(true);

        // Cleanup function to reset state when component unmounts
        return () => {
            if (resetState) {
                resetState();
            }
            appContext?.setEditInProgress(false);
            appContext?.setEditSaveHandler(null);
            appContext?.setEditCancelHandler(null);
        };
    }, [uid, categoryId, industryId, channelKey]);

    // Intercept React Router in-app navigation (browser back/forward, Links,
    // programmatic navigate() elsewhere in the app)
    const blocker = useBlocker(useCallback(() => {
        if (allowNavigationRef.current) {
            return false;
        }
        return hasUnsavedChanges;
    }, [hasUnsavedChanges]));

    useEffect(() => {
        if (blocker.state === "blocked") {
            setShowCancelDialog(true);
        }
    }, [blocker.state, setShowCancelDialog]);

    const handleCancelClick = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowCancelDialog(true);
        } else {
            navigate("/gms/substitutions/");
        }
    }, [hasUnsavedChanges, setShowCancelDialog, navigate]);

    const handleSave = useCallback(async (): Promise<boolean> => {
        if (saveInProgress) return false;
        setSaveInProgress(true);
        try {
            if (!iterationPayload) {
                toast.error('No data to save');
                return false;
            }

            if (!iterationPayload.category_id) {
                toast.error('Please select a category');
                return false;
            }

            // Check if any iteration has empty conditions array
            const hasEmptyIterations = iterationPayload.iterations.some(iteration =>
                !iteration.conditions || iteration.conditions.length === 0
            );

            if (hasEmptyIterations) {
                toast.error('Each iteration must have at least one condition');
                return false;
            }

            // Validate attribute sorting - if it exists, it must have attribute_id and attribute_name
            const hasInvalidAttributeSorting = iterationPayload.iterations.some(iteration => {
                if (iteration.attribute_sorting && iteration.attribute_sorting.length > 0) {
                    return iteration.attribute_sorting.some(sortItem =>
                        !sortItem.attribute_id ||
                        !sortItem.attribute_name ||
                        sortItem.attribute_id.trim() === '' ||
                        sortItem.attribute_name.trim() === ''
                    );
                }
                return false;
            });

            if (hasInvalidAttributeSorting) {
                toast.error('All attribute sorting items must have attribute selected');
                return false;
            }

            // Check for missing required fields in conditions
            const hasEmptyConditions = iterationPayload.iterations.some(iteration => {
                const checkConditions = (conditions: ConditionNode[]): boolean => {
                    return conditions.some(condition => {
                        if (condition.type === 'operations') {
                            const opCondition = condition as OperationCondition;

                            // Always required fields
                            if (!opCondition.attribute_id ||
                                !opCondition.value_type ||
                                opCondition.operand === null ||
                                opCondition.operand === undefined) {
                                return true;
                            }

                            // Operator required only when value_type is NOT "Multiplier"
                            if (opCondition.value_type !== "Multiplier" && !opCondition.operator) {
                                return true;
                            }

                            return false;
                        } else if (condition.type === 'condition') {
                            const nestedCondition = condition as NestedCondition;
                            return checkConditions(nestedCondition.conditions);
                        }
                        return false;
                    });
                };
                return checkConditions(iteration.conditions);
            });

            if (hasEmptyConditions) {
                toast.error('Please fill in all required fields for conditions');
                return false;
            }

            let payloadToUpdate = iterationPayload;

            if (!isEditMode) {
                if (!industryId || !channelKey || !iterationPayload.category_id || !iterationPayload.category_name) {
                    toast.error('Missing required information for creation');
                    return false;
                }

                const createResponse = await createIterationMethod(industryId, channelKey, iterationPayload.category_id, iterationPayload.category_name);
                if (createResponse.done && createResponse?.data?.id) {
                    payloadToUpdate = {
                        ...iterationPayload,
                        id: createResponse.data.id
                    };
                    setIterationPayload(payloadToUpdate);
                } else {
                    toast.error("Failed to create iteration method");
                    return false;
                }
            }

            const response = await updateIterationMethod(payloadToUpdate);
            if (response.done) {
                toast.success(isEditMode ? "Updated iteration method successfully" : "Created iteration method successfully");
                return true;
            } else {
                toast.error(response.message || "Save failed");
                return false;
            }
        }
        finally {
            setSaveInProgress(false);
        }
    }, [iterationPayload, isEditMode, industryId, channelKey, setIterationPayload]);

    // Register AppContext handlers for left menu unsaved changes modal
    useEffect(() => {
        appContext?.setEditSaveHandler(() => () => handleSave());
        appContext?.setEditCancelHandler(() => () => {
            allowNavigationRef.current = true;
        });
    }, [handleSave]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cancel dialog handlers
    const handleDialogCancel = useCallback(() => {
        setShowCancelDialog(false);
        if (blocker.state === "blocked") blocker.reset();
    }, [setShowCancelDialog, blocker]);

    const handleDialogSave = useCallback(async () => {
        if (saveInProgress) return;
        const saveSuccess = await handleSave();
        setShowCancelDialog(false);

        if (saveSuccess) {
            allowNavigationRef.current = true;
            if (blocker.state === "blocked") {
                blocker.proceed();
            } else {
                navigate("/gms/substitutions/");
            }
        }
    }, [handleSave, setShowCancelDialog, navigate, blocker]);

    const handleConfirmCancel = useCallback(() => {
        setShowCancelDialog(false);
        allowNavigationRef.current = true;
        if (blocker.state === "blocked") {
            blocker.proceed();
        } else {
            navigate("/gms/substitutions/");
        }
    }, [setShowCancelDialog, navigate, blocker]);

    const handleSaveClick = useCallback(async () => {
        if (saveInProgress) return false;
        const saveSuccess = await handleSave();
        if (saveSuccess) {
            allowNavigationRef.current = true;
            navigate("/gms/substitutions/");
        }
        setSaveInProgress(false);
    }, [handleSave, navigate]);

    // Determine page title based on mode
    const pageTitle = isEditMode === false ? CREATE : isEditMode === true ? EDIT : "";

    return (
        <>
            <div className="tw-w-full" data-testid="iteration-configuration">
                {/* Breadcrumb navigation — the crumb runs handleCancelClick so the
                    unsaved-changes guard still owns the exit, exactly as before. */}
                <Breadcrumbs
                    className="tw-mb-2"
                    items={[
                        {
                            label: 'Iteration Method',
                            onClick: handleCancelClick,
                            'data-testid': 'iteration-iterationconfigurationprovider-span-1',
                        },
                        { label: pageTitle },
                    ]}
                />

                {/* Main content with title and buttons */}
                <div className="tw-flex tw-flex-row tw-items-center tw-justify-between">
                    <Typography
                        variant="display"
                        as="h1"
                        id="cs-title"
                        data-testid="page-title"
                        className="tw-text-ink tw-text-left tw-my-3.5"
                    >
                        {pageTitle}
                    </Typography>
                    <div className="tw-flex tw-flex-row tw-gap-3">
                        <Button
                            variant="secondary"
                            onClick={handleCancelClick}
                            data-testid="cancel-button"
                            disabled={saveInProgress}
                        >
                            {CANCEL}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSaveClick}
                            data-testid="save-button"
                            disabled={!iterationPayload?.category_id || saveInProgress}
                            loading={saveInProgress}
                        >
                            {saveInProgress ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </div>
                {/* The header hairline the other CS pages draw below their title rows. */}
                <div className="tw-w-full tw-border-b tw-border-edge"></div>
            </div>

            <CategorySelector
                id="category-selection"
                categoryId={categoryId}
            />

            {loading && (
                <div className="tw-flex tw-justify-center tw-py-10">
                    <Spinner size="lg" label="Loading iteration" showLabel data-testid="iteration-loading" />
                </div>
            )}
            {!loading && iterationPayload?.category_id && <IterationComponent />}

            {/* Cancel Confirmation Dialog */}
            {(showCancelDialog || blocker.state === "blocked") && <ConfirmModal
                id="iteration-unsaved-changes-modal"
                title="Unsaved Changes"
                message="You have unsaved changes to this computed substitution. What would you like to do?"
                onClose={handleDialogCancel}
                onConfirm={handleDialogSave}
                ternaryMethod={handleConfirmCancel}
                ternaryText="Discard changes"
                confirmText="Save Changes"
                cancelText="Cancel"
                saveInProgress={saveInProgress}
            />}
        </>
    );
};

const IterationConfigurationProvider = (props: any) => {
    return (
        <IterationContextProvider>
            <IterationConfiguration {...props} />
        </IterationContextProvider>
    );
};

export default IterationConfigurationProvider;